import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Button,
  Typography,
  Stack,
  RadioGroup,
  Radio,
  Modal,
  Slide,
  Tab,
  Tabs,
  CircularProgress,
} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as ArrowrightIcon } from "../../../../vendor/_new/icon/sites/arrow_right.svg";
import { ReactComponent as InsecureIcon } from "../../../../vendor/_new/icon/sites/insecure.svg";
import { ReactComponent as AdaptiveIcon } from "../../../../vendor/_new/icon/sites/adaptive.svg";
import { ReactComponent as Adaptivee2eIcon } from "../../../../vendor/_new/icon/sites/adaptive_e2e.svg";
import { ReactComponent as Advancede2eIcon } from "../../../../vendor/_new/icon/sites/advanced_e2e.svg";
import { ReactComponent as InstallcertIcon } from "../../../../vendor/_new/icon/sites/www.svg";

import { GreenSwitch } from "../../../common/Greens";
import StickySaveBar from "../../../common/StickySaveBar";
import { collectObjectDiff, enabledLabel } from "../../../common/stickySaveBarUtils";
import colors from "../../../../utils/colors";
import useSSLConfig from "../../../../hooks/user/useSSLConfig";
import useAuth from "../../../../hooks/useAuth";
import { CertificateType, SslType, UserType } from "../../../../utils/constants";
import useWAFConfig from "../../../../hooks/user/useWAFConfig";
import usePrevious from "../../../../hooks/usePrevious";
import useSite from "../../../../hooks/user/useSite";
import SSLConfigurationFree from "./SSLConfigurationFree";
import SSLConfigurationUpload from "./SSLConfigurationUpload";
import SSLConfigurationGenerateOrigin from "./SSLConfigurationGenerateOrigin";
import { useNotification } from "../../../../contexts/NotificationContext";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../super/design";

export const SSLTypes = [
  {
    type: SslType.OFF,
    title: "InSecure",
    description: "No encryption applied.",
    label: "Least secured",
  },
  {
    type: SslType.FLEXIBLE,
    title: "Adaptive",
    description: "Encrypts traffic between the browser and Layerd AI.",
    label: "Medium security",
  },
  {
    type: SslType.FULL,
    title: "Adaptive E2E",
    description: "Encrypts end-to-end, using a self signed certificate on the server.",
    label: "Secured",
  },
  {
    type: SslType.FULL_STRICT,
    title: "Advanced E2E",
    description: "Encrypts end-to-end, but requires a trusted CA or Layerd AI Origin CA certificate on the server.",
    label: "Most secured",
  },
];

export const getSSLTypeIcon = (type, isSelected) => {
  switch (type) {
    case SslType.OFF:
      return <InsecureIcon style={{ color: isSelected ? colors.neutral[1000] : colors.neutral[600] }} />;
    case SslType.FLEXIBLE:
      return <AdaptiveIcon style={{ color: isSelected ? colors.neutral[1000] : colors.neutral[600] }} />;
    case SslType.FULL:
      return <Adaptivee2eIcon style={{ color: isSelected ? colors.neutral[1000] : colors.neutral[600] }} />;
    case SslType.FULL_STRICT:
      return <Advancede2eIcon style={{ color: isSelected ? colors.neutral[1000] : colors.neutral[600] }} />;
    default:
      return <InsecureIcon style={{ color: isSelected ? colors.neutral[1000] : colors.neutral[600] }} />;
  }
};

const certInstallOptions = [
  {
    value: "free",
    title: "Universal",
    description: "Issue a managed certificate for your domain.",
  },
  {
    value: "upload",
    title: "Upload SSL certificate",
    description: "Provide your own CA-signed certificate and key.",
  },
  {
    value: "generate",
    title: "Generate Origin certificate",
    description: "Create an origin certificate for strict end-to-end encryption.",
  },
];

const sslTypeTitle = (id) => SSLTypes.find((t) => t.type === id)?.title ?? String(id);

const SSL_CONFIG_DIFF_FIELDS = [
  { key: "ssl_type", label: "Encryption mode", format: sslTypeTitle },
  { key: "https_redirect_enabled", label: "HTTP → HTTPS redirection", format: enabledLabel },
];

const SSLConfiguration = ({ handleNext, siteUid }) => {
  const { cursite } = useSite();
  const { sslConfig, sslTypeChange, configSslSetting } = useSSLConfig();
  const { getWAFConfig } = useWAFConfig();
  const prevSslConfig = usePrevious(sslConfig);
  const { userType } = useAuth();
  const { showError, showSuccess } = useNotification();

  const [sslType, setSslType] = useState(SslType.OFF);
  const [certType, setCertType] = useState("");
  const [httpsRedirectEnabled, setHttpsRedirectEnabled] = useState(false);
  const [plainBaseline, setPlainBaseline] = useState(null);
  const [plainDraft, setPlainDraft] = useState(null);
  const [isApplyingPlain, setIsApplyingPlain] = useState(false);

  const [openInstallCertModal, setOpenInstallCertModal] = useState(false);
  const handleInstallCertOpen = () => setOpenInstallCertModal(true);
  const handleInstallCertClose = () => setOpenInstallCertModal(false);
  const [installCertBaselineKey, setInstallCertBaselineKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (openInstallCertModal) setInstallCertBaselineKey((k) => k + 1);
  }, [openInstallCertModal]);

  const installCertSnapshot = useMemo(() => ({ certType }), [certType]);
  const installCertDirty = useDrawerBaselineDirty(openInstallCertModal, installCertSnapshot, installCertBaselineKey);
  const { requestClose: requestInstallCertClose, confirmDialog: installCertConfirmDialog } = useGuardedDrawerClose(
    handleInstallCertClose,
    installCertDirty,
    { disabled: isSubmitting },
  );

  // SSL configuration
  const _selectSSLType = useCallback(
    (value) => {
      if (userType === UserType.ORG_USER) return;
      setSslType(value);
    },
    [userType]
  );
  const _httpsRedirectChange = useCallback(
    (enable) => {
      if (userType === UserType.ORG_USER) return;
      setHttpsRedirectEnabled(!!enable);
    },
    [userType]
  );
  // const httpsRedirectChange = () => {
  //   _httpsRedirectChange(!https_redirect_enabled);
  // };

  /// WAF configuration
  const continueToWAF = async () => {
    if (plainBaseline && plainDraft) {
      const dirty = plainBaseline.ssl_type !== plainDraft.ssl_type
        || !!plainBaseline.https_redirect_enabled !== !!plainDraft.https_redirect_enabled;
      if (dirty) {
        showError("Apply changes before continuing");
        return;
      }
    }
    const effectiveSslType = sslConfig?.ssl_type ?? sslType;
    const sslCertificateReady =
      !!sslConfig?.certs?.host?.length || sslConfig?.certs?.type === CertificateType.SENSE_GUARD;

    if (effectiveSslType !== SslType.OFF && !certType && !sslCertificateReady) {
      showError("Please select an SSL certificate type");
      return;
    }
    if (effectiveSslType !== SslType.OFF && !sslCertificateReady) {
      showError("Please verify domain and install SSL certificate");
      return;
    }
    if (SslType.OFF < effectiveSslType && CertificateType.CUSTOM === sslConfig?.certs?.type && !sslConfig?.certs?.host?.length) {
      showError("Please upload or generate SSL certificates");
      return;
    }
    if (SslType.FULL_STRICT === effectiveSslType && (!sslConfig?.sg_certs?.host || !sslConfig?.sg_certs?.validTo)) {
      showError("Please generate SSL origin certificates");
      return;
    }
    setIsSubmitting(true);
    await getWAFConfig(siteUid, true);
    setIsSubmitting(false);
    handleNext();
  };

  useEffect(() => {
    async function _refreshSslConfig() {
      if (sslConfig) {
        setSslType(sslConfig?.ssl_type);
        setHttpsRedirectEnabled(sslConfig?.https_redirect_enabled === true);
        const next = { ssl_type: sslConfig?.ssl_type, https_redirect_enabled: sslConfig?.https_redirect_enabled === true };
        setPlainBaseline(next);
        setPlainDraft(next);
        const inferredCertType =
          sslConfig?.certs?.type === CertificateType.SENSE_GUARD
            ? "free"
            : sslConfig?.certs?.type === CertificateType.CUSTOM
            ? sslConfig?.sg_certs?.host
              ? "generate"
              : "upload"
            : "";
        if (inferredCertType) {
          setCertType(inferredCertType);
        }
        if (
          SslType.OFF === prevSslConfig?.ssl_type &&
          !prevSslConfig?.certs?.host?.length &&
          (0 < sslConfig?.certs?.host?.length ||
            (CertificateType.SENSE_GUARD === sslConfig?.certs?.type && SslType.OFF === sslConfig?.ssl_type))
        ) {
          // Change SSL type even if wildcard certificate is not ready.
          setSslType(SslType.FLEXIBLE);
          setHttpsRedirectEnabled(true);
        }
      }
    }
    _refreshSslConfig();
  }, [sslConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!plainBaseline) return;
    setPlainDraft({ ssl_type: sslType, https_redirect_enabled: httpsRedirectEnabled });
  }, [httpsRedirectEnabled, plainBaseline, sslType]);

  const plainModifiedFields = useMemo(
    () => collectObjectDiff(plainBaseline, plainDraft, SSL_CONFIG_DIFF_FIELDS),
    [plainBaseline, plainDraft],
  );
  const plainModifiedCount = plainModifiedFields.length;

  const effectiveSslType = sslConfig?.ssl_type ?? sslType;
  const sslCertificateReady =
    !!sslConfig?.certs?.host?.length || sslConfig?.certs?.type === CertificateType.SENSE_GUARD;
  const originCertificateReady =
    !!sslConfig?.sg_certs?.host && !!sslConfig?.sg_certs?.validTo;
  const canContinue =
    !isSubmitting &&
    (effectiveSslType === SslType.OFF || sslCertificateReady) &&
    (effectiveSslType !== SslType.FULL_STRICT || originCertificateReady);

  let continueBlockedReason = null;
  if (!canContinue && !isSubmitting) {
    if (effectiveSslType !== SslType.OFF && !sslCertificateReady) {
      continueBlockedReason = "Please verify domain and install SSL certificate";
    } else if (effectiveSslType === SslType.FULL_STRICT && !originCertificateReady) {
      continueBlockedReason = "Please generate SSL origin certificates";
    }
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800], pb: "8px" }}>
          To protect your application and user data, follow the steps below to enable SSL/TLS encryption and establish a secure connection.
        </Typography>
        <RadioGroup value={sslType}>
          <Grid container spacing={4} sx={{ mt: 0 }}>
            {SSLTypes?.map((item, index) => (
              <Grid key={`ssl_type_${index}`} item xs={12} md={6}>
                <Stack
                  spacing={1}
                  onClick={() => _selectSSLType(item.type)}
                  sx={{
                    p: 4,
                    cursor: "pointer",
                    width: "100%",
                    height: "-webkit-fill-available",
                    borderRadius: "8px",
                    backgroundColor: sslType === item.type ? colors.secondary["alpha"] : "inherit",
                    border: `solid 1px ${sslType === item.type ? colors.secondary[200] : colors.neutral[200]}`,
                  }}
                >
                  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                    {getSSLTypeIcon(item.type, item.type === sslType)}
                    <Stack
                      alignItems={"center"}
                      justifyContent={"center"}
                      sx={{
                        py: 0.5,
                        px: 3,
                        border: `solid 1px ${colors.primary[300]}`,
                        backgroundColor: colors.primary["alpha"],
                        borderRadius: "100px",
                      }}
                    >
                      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                        {item.label}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={6}>
                    <Stack>
                      <Typography
                        sx={ { fontSize: 16, lineHeight: "24px", color: colors.neutral[1000], fontWeight: "medium" }}
                      >
                        {item.title}
                      </Typography>
                      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
                        {item.description}
                      </Typography>
                    </Stack>
                    <Radio
                      value={item.type}
                      size="small"
                      sx={{
                        color: colors.neutral[300], // border / unchecked color
                        "&.Mui-checked": {
                          color: colors.secondary[300], // checkmark & filled color
                        },
                      }}
                    />
                  </Stack>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
        {sslType !== SslType.OFF && (
          <>
            <Typography
              sx={ { fontSize: 16, lineHeight: "24px", color: colors.neutral[1000], fontWeight: "medium", pt: 6, py: 4 }}
            >
              Install SSL certificate
            </Typography>
            <RadioGroup value={certType}>
              <Grid container spacing={4}>
                {certInstallOptions.map((item) => (
                  <Grid key={item.value} item xs={12} md={4}>
                    <Stack
                      spacing={2}
                      onClick={() => {
                        setCertType(item.value);
                        handleInstallCertOpen();
                      }}
                      sx={{
                        p: 4,
                        cursor: "pointer",
                        borderRadius: "8px",
                        border: `solid 1px ${certType === item.value ? colors.secondary[200] : colors.neutral[200]}`,
                        backgroundColor: certType === item.value ? colors.secondary["alpha"] : "#fff",
                        height: "100%",
                      }}
                    >
                      <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={2}>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000], fontWeight: "medium" }}>
                          {item.title}
                        </Typography>
                        <Radio
                          value={item.value}
                          size="small"
                          sx={{
                            color: colors.neutral[300],
                            "&.Mui-checked": {
                              color: colors.secondary[300],
                            },
                          }}
                        />
                      </Stack>
                      <Typography sx={ { fontSize: 13, lineHeight: "20px", color: colors.neutral[700] }}>
                        {item.description}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{
                mt: 4,
                p: 4,
                borderRadius: "8px",
                border: `solid 1px ${colors.neutral[200]}`,
              }}
            >
              <Stack spacing={0.5}>
                <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000], fontWeight: "medium" }}>
                  HTTP to HTTPS redirection
                </Typography>
                <Typography sx={ { fontSize: 13, lineHeight: "20px", color: colors.neutral[700] }}>
                  Redirect all HTTP traffic to HTTPS.
                </Typography>
              </Stack>
              <GreenSwitch
                checked={httpsRedirectEnabled}
                disabled={userType === UserType.ORG_USER}
                onChange={(e) => _httpsRedirectChange(e.target.checked)}
              />
            </Stack>
          </>
        )}
        <Box sx={{ pt: 6 }}>
          <Stack spacing={1}>
            <Button
              id="ssl-continue-button"
              endIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ArrowrightIcon style={{ color: "#fff" }} />}
              onClick={continueToWAF}
              disabled={!canContinue}
              aria-describedby={continueBlockedReason ? "ssl-continue-blocked-reason" : undefined}
              sx={{
                height: 36,
                backgroundColor: "#6371F7",
                border: "1px solid #6371F7",
                borderRadius: "8px",
                fontSize: 14,
                lineHeight: "21px",
                color: "#fff",
                fontWeight: "regular",
                textTransform: "none",
                px: 4,
                "&:hover": {
                  backgroundColor: "#4959ee",
                  borderColor: "#4959ee",
                  color: "#fff",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#6371F7",
                  borderColor: "#6371F7",
                  color: "#fff",
                  opacity: 0.7,
                },
              }}
            >
              {isSubmitting ? "Loading..." : "Continue"}
            </Button>
            {continueBlockedReason && (
              <Typography
                id="ssl-continue-blocked-reason"
                sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}
              >
                {continueBlockedReason}
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>

      <StickySaveBar
        open={plainModifiedCount > 0}
        modifiedCount={plainModifiedCount}
        modifiedFields={plainModifiedFields}
        onDiscard={() => {
          if (!plainBaseline) return;
          setSslType(plainBaseline.ssl_type);
          setHttpsRedirectEnabled(!!plainBaseline.https_redirect_enabled);
        }}
        onApply={async () => {
          if (userType === UserType.ORG_USER) return;
          if (!plainBaseline || !plainDraft || !siteUid) return;
          setIsApplyingPlain(true);
          try {
            if (plainDraft.ssl_type !== plainBaseline.ssl_type) {
              await sslTypeChange(siteUid, plainDraft.ssl_type);
            }
            if (!!plainDraft.https_redirect_enabled !== !!plainBaseline.https_redirect_enabled) {
              if (cursite?.site_id && siteUid) {
                await configSslSetting(siteUid, "httpsRedirectChange", { site_id: cursite?.site_id, enable: !!plainDraft.https_redirect_enabled });
              }
            }
            showSuccess("Saved");
          } catch (e) {
            showError(e?.message || "Failed to save changes");
          } finally {
            setIsApplyingPlain(false);
          }
        }}
        isApplying={isApplyingPlain}
      />

      <Modal open={openInstallCertModal} onClose={requestInstallCertClose}>
        <Slide direction="left" in={openInstallCertModal} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 4, borderBottom: `1px solid ${colors.neutral[200]}` }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InstallcertIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                </Box>
                <Stack spacing={0.5}>
                  <Typography
                    sx={ { fontWeight: "medium", fontSize: 18, lineHeight: "24px", color: colors.neutral[1000] }}
                  >
                    Install SSL certificate
                  </Typography>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Install your SSL/TLS certificate to enable encrypted and secure connections.
                  </Typography>
                </Stack>
              </Stack>
              <Box onClick={requestInstallCertClose} sx={{ cursor: "pointer" }}>
                <CloseIcon style={{ color: colors.neutral[600] }} />
              </Box>
            </Stack>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={3} sx={{ px: 4, pt: 3 }}>
              <Tabs
                value={certType}
                onChange={(_, v) => setCertType(v)}
                sx={{
                  minHeight: "inherit",
                  height: 32,
                  "& .MuiTabs-flexContainer": { border: "none", px: 0, py: 0, gap: "8px", height: 32 },
                  "& .MuiTabs-indicator": { height: "1px", backgroundColor: colors.secondary[300] },
                  "& button": { p: 0, minWidth: "inherit", minHeight: "inherit" },
                  "& button .MuiTypography-root": { color: colors.neutral[700] },
                  "& .Mui-selected .MuiTypography-root": { color: colors.neutral[1000] },
                }}
              >
                <Tab
                  value={"free"}
                  label={<Typography sx={ { fontSize: 14 }}>Generate SSL</Typography>}
                  sx={{ width: 125 }}
                />
                <Tab
                  value={"upload"}
                  label={<Typography sx={ { fontSize: 14 }}>Upload SSL</Typography>}
                  sx={{ width: 125 }}
                />
                <Tab
                  value={"generate"}
                  label={<Typography sx={ { fontSize: 14 }}>Generate Origin</Typography>}
                  sx={{ width: 125 }}
                />
              </Tabs>
            </Stack>
            <Box sx={{ py: 4, px: 4, height: `calc(100vh - 200px)` }}>
              <ReactPerfectScrollbar>
                <Stack justifyContent={"space-between"} spacing={4} sx={{ pr: 4, height: "100%" }}>
                  {certType === "free" && (
                    <SSLConfigurationFree open={openInstallCertModal} siteUid={siteUid} handleClose={handleInstallCertClose} />
                  )}
                  {certType === "upload" && (
                    <SSLConfigurationUpload open={openInstallCertModal} siteUid={siteUid} handleClose={handleInstallCertClose} />
                  )}
                  {certType === "generate" && (
                    <SSLConfigurationGenerateOrigin open={openInstallCertModal} siteUid={siteUid} handleClose={handleInstallCertClose} />
                  )}
                </Stack>
              </ReactPerfectScrollbar>
            </Box>
          </Box>
        </Slide>
      </Modal>
      {installCertConfirmDialog}
    </>
  );
};
export default SSLConfiguration;
